import { Eyebrow, MaskLine, Reveal } from "@/components/site/Reveal";
import { BuyOrQuote, DetailsLink, Highlights, PriceTag } from "@/components/site/ProductBits";
import { BladeMark } from "@/components/site/Logo";
import { useProducts } from "@/hooks/useProducts";

function SystemCard({ p, onQuote, delay }) {
  return (
    <Reveal delay={delay} className="group border border-white/10 bg-surface" data-testid={`system-card-${p.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={p.action_image || p.image} alt={p.name} className="h-full w-full object-cover grayscale-[40%] transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
        <span className="absolute left-4 top-4 bg-black/80 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-volt">{p.model}</span>
      </div>
      <div className="p-6 md:p-8">
        <h3 className="font-display text-3xl md:text-4xl font-extrabold uppercase leading-none">{p.name}</h3>
        <p className="mt-3 text-sm text-zinc-300">{p.tagline} {p.description}</p>
        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {(p.features || []).slice(0, 4).map((f) => (
            <li key={f} className="font-mono text-[11px] uppercase tracking-wider text-zinc-300">+ {f}</li>
          ))}
        </ul>
        <Highlights product={p} className="mt-5 sm:grid-cols-1" />
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
          <PriceTag product={p} />
          <div className="flex items-center gap-3">
            <DetailsLink slug={p.slug} />
            <BuyOrQuote product={p} onQuote={onQuote} />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function AccessoryCard({ p, onQuote, delay }) {
  return (
    <Reveal delay={delay} className="group flex flex-col border border-white/10 bg-surface" data-testid={`accessory-card-${p.slug}`}>
      <div className="relative aspect-square overflow-hidden bg-white">
        {p.image ? (
          <img src={p.image} alt={p.name} className="h-full w-full object-contain p-6 transition-transform duration-700 group-hover:scale-110" />
        ) : (
          <div className="grid h-full place-items-center bg-[#141418]">
            <BladeMark className="h-24 w-24 opacity-80 transition-transform duration-[1.5s] group-hover:rotate-180" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{p.model}</p>
        <h4 className="mt-1 font-display text-2xl font-bold uppercase leading-tight">{p.name}</h4>
        <PriceTag product={p} className="mt-2 text-xs" />
        <div className="mt-auto pt-4">
          <BuyOrQuote product={p} onQuote={onQuote} />
        </div>
      </div>
    </Reveal>
  );
}

export function Ecosystem({ onQuote }) {
  const { products } = useProducts();
  const systems = products.filter((p) => p.category === "system");
  const acc = products.filter((p) => p.category === "accessory");
  return (
    <section id="ecosystem" data-testid="ecosystem-section" className="bg-obsidian py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Eyebrow>Universal Power Ecosystem</Eyebrow>
        <h2 className="mt-5 mb-12 font-display text-[12.5vw] sm:text-6xl md:text-8xl font-black uppercase leading-[0.85]" style={{ fontWeight: 900 }}>
          <MaskLine inView>Power the</MaskLine>
          <MaskLine inView delay={0.1}><span className="text-volt">whole rig.</span></MaskLine>
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {systems.map((p, i) => <SystemCard key={p.slug} p={p} onQuote={onQuote} delay={i * 0.08} />)}
        </div>
        <div className="mt-20 mb-8 flex items-end justify-between border-b border-white/10 pb-4">
          <h3 className="font-display text-4xl md:text-5xl font-extrabold uppercase">System Accessories</h3>
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">{acc.length} items</span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {acc.map((p, i) => <AccessoryCard key={p.slug} p={p} onQuote={onQuote} delay={(i % 5) * 0.05} />)}
        </div>
      </div>
    </section>
  );
}
